/**
 * Utilitários para extrair informações de status dos domínios de propriedades
 * Implementação com regras claras e simplificadas para cada domínio
 */
const DEBUG = import.meta.env.VITE_DEBUG === 'true';
const VERBOSE = import.meta.env.VITE_DEBUG_VERBOSE === 'true';

// Constantes de status para cada domínio
const STATUS = {
  CAR: {
    ATIVO: 'Ativo',
    PENDENTE: 'Pendente',
    SUSPENSO: 'Suspenso',
  },
  SIGEF: {
    CERTIFICADA: 'Certificada',
    REGISTRADA: 'Registrada',
    NAO_REGISTRADA: 'Não registrada',
  },
  SNCR: {
    REGISTRADA: 'Registrada',
    NAO_REGISTRADA: 'Não registrada',
  },
  CAFIR: {
    REGISTRADA: 'Registrada',
    NAO_REGISTRADA: 'Não registrada',
  },
  CCIR: {
    PRESENTE: 'Presente',
    INEXISTENTE: 'Inexistente',
  },
};

/**
 * Determina se há dados consolidados do CCIR no snapshot
 * @param {Object} propertyData
 * @returns {string} Status "Presente" ou "Inexistente"
 */
export const extractCcirStatus = (propertyData) => {
  try {
    const props = propertyData?.properties;
    if (!props || typeof props !== 'object') return STATUS.CCIR.INEXISTENTE;

    const key = Object.keys(props).find((k) => {
      const lower = k.toLowerCase();
      return lower === 'serpro_ccir' || lower === 'ccir';
    });

    if (!key) return STATUS.CCIR.INEXISTENTE;

    const raw = props[key];
    if (Array.isArray(raw)) {
      return raw.length > 0 && raw.some((item) => item && Object.keys(item).length > 0)
        ? STATUS.CCIR.PRESENTE
        : STATUS.CCIR.INEXISTENTE;
    }

    if (raw && typeof raw === 'object' && Object.keys(raw).length > 0) {
      return STATUS.CCIR.PRESENTE;
    }

    return STATUS.CCIR.INEXISTENTE;
  } catch (error) {
    if (DEBUG) console.error('[domainStatusExtractor] Erro ao extrair status do CCIR:', error);
    return STATUS.CCIR.INEXISTENTE;
  }
};

// Constantes de cor para status
const COLOR = {
  SUCCESS: 'success', // Verde
  WARNING: 'warning', // Amarelo
  ERROR: 'error', // Vermelho
  DEFAULT: 'default', // Cinza
};

/**
 * Função auxiliar para extrair status do CAR
 * @param {Object} propertyData - Dados da propriedade
 * @returns {string} Status do CAR ou null
 */
export const extractCarStatus = (propertyData) => {
  // Verifica se existe array CAR com pelo menos um elemento
  if (
    !propertyData?.properties?.car ||
    !Array.isArray(propertyData.properties.car) ||
    propertyData.properties.car.length === 0
  ) {
    return null;
  }

  // Verificar situacao_do_cadastro no primeiro CAR
  const situacao = propertyData.properties.car[0]?.details?.situacao_do_cadastro;

  // Aplicar regras para situação do CAR
  if (situacao === 'AT') return STATUS.CAR.ATIVO;
  if (situacao === 'RE' || situacao === 'PE') return STATUS.CAR.PENDENTE;
  if (situacao === 'SU') return STATUS.CAR.SUSPENSO;

  return null;
};

/**
 * Função auxiliar para extrair status do SIGEF
 * @param {Object} propertyData - Dados da propriedade
 * @returns {string} Status do SIGEF
 */
export const extractSigefStatus = (propertyData) => {
  try {
    // Verifica se existe array SIGEF com pelo menos um elemento
    const sigefRaw = propertyData?.properties?.sigef;
    if (!sigefRaw || (Array.isArray(sigefRaw) && sigefRaw.length === 0)) {
      return STATUS.SIGEF.NAO_REGISTRADA;
    }

    // Verifica status para cada item do SIGEF
    const parcels = [];
    if (Array.isArray(sigefRaw)) {
      for (const item of sigefRaw) {
        if (item?.parcels && Array.isArray(item.parcels)) {
          parcels.push(...item.parcels);
        } else {
          parcels.push(item);
        }
      }
    } else if (sigefRaw?.parcels && Array.isArray(sigefRaw.parcels)) {
      parcels.push(...sigefRaw.parcels);
    } else if (sigefRaw?.details && Array.isArray(sigefRaw.details)) {
      parcels.push(...sigefRaw.details);
    } else if (sigefRaw) {
      parcels.push(sigefRaw);
    }

    const getSituacao = (obj) => {
      if (!obj || typeof obj !== 'object') return null;
      const candidates = [
        obj['situação'],
        obj['SITUAÇÃO'],
        obj['Situação'],
        obj.situacao,
        obj.situacao_do_imovel,
        obj['situação_do_imóvel'],
        obj.status_desc,
      ];
      for (const c of candidates) {
        if (typeof c === 'string' && c.trim()) return c.trim();
      }
      return null;
    };

    const situacoes = parcels.map(getSituacao);
    const nonEmptySituacoes = situacoes.filter(Boolean);
    if (nonEmptySituacoes.length === 0) {
      // Há dados SIGEF, porém nenhuma parcela tem campo de situação -> não certificada
      return STATUS.SIGEF.REGISTRADA;
    }

    if (nonEmptySituacoes.length < parcels.length) {
      // Algumas parcelas não possuem situação definida -> não atende "todos"
      return STATUS.SIGEF.REGISTRADA;
    }

    const allCertified = nonEmptySituacoes.every((s) => s.toLowerCase().includes('certific'));
    return allCertified ? STATUS.SIGEF.CERTIFICADA : STATUS.SIGEF.REGISTRADA;
  } catch (error) {
    if (DEBUG) console.error('[domainStatusExtractor] Erro ao extrair status do SIGEF:', error);
    return STATUS.SIGEF.NAO_REGISTRADA;
  }
};

/**
 * Função auxiliar para extrair status do CAFIR
 * Regra simplificada: se tem NIRF = Registrada, caso contrário = Não registrada
 * @param {Object} propertyData - Dados da propriedade
 * @returns {string} Status do CAFIR
 */
export const extractCafirStatus = (propertyData) => {
  try {
    const cafirData = propertyData?.properties?.CAFIR || propertyData?.properties?.cafir;
    if (!cafirData) return STATUS.CAFIR.NAO_REGISTRADA;

    const nirfFields = ['nirf', 'NIRF', 'codigo_nirf', 'CODIGO_NIRF'];
    const hasNirfInObject = (obj) => {
      if (!obj || typeof obj !== 'object') return false;
      for (const f of nirfFields) {
        const v = obj?.[f];
        if (typeof v === 'string' && v.trim()) return true;
      }
      // procurar em details (array de objetos)
      if (Array.isArray(obj.details)) {
        for (const d of obj.details) {
          for (const f of nirfFields) {
            const v = d?.[f];
            if (typeof v === 'string' && v.trim()) return true;
          }
        }
      }
      // busca profunda limitada: varrer chaves de 1º nível que sejam objetos
      for (const k of Object.keys(obj)) {
        const v = obj[k];
        if (v && typeof v === 'object') {
          for (const f of nirfFields) {
            const vv = v?.[f];
            if (typeof vv === 'string' && vv.trim()) return true;
          }
        }
      }
      return false;
    };

    if (Array.isArray(cafirData)) {
      for (const item of cafirData) {
        if (hasNirfInObject(item)) return STATUS.CAFIR.REGISTRADA;
      }
      return STATUS.CAFIR.NAO_REGISTRADA;
    }

    return hasNirfInObject(cafirData) ? STATUS.CAFIR.REGISTRADA : STATUS.CAFIR.NAO_REGISTRADA;
  } catch (error) {
    if (DEBUG) console.error('[domainStatusExtractor] Erro ao extrair status do CAFIR:', error);
    return STATUS.CAFIR.NAO_REGISTRADA;
  }
};

/**
 * Função auxiliar para extrair status do SNCR
 * Regra simplificada: se tem titulares = Registrada, caso contrário = Não registrada
 * @param {Object} propertyData - Dados da propriedade
 * @returns {string} Status do SNCR
 */
export const extractSncrStatus = (propertyData) => {
  try {
    const sncrData = propertyData?.properties?.SNCR || propertyData?.properties?.sncr;
    if (!sncrData) return STATUS.SNCR.NAO_REGISTRADA;

    const hasTitulares = (obj) => Array.isArray(obj?.titulares) && obj.titulares.length > 0;
    const hasCodigo = (obj) => Boolean(obj?.codigo_imo || obj?.codigo || obj?.codigo_imovel_incra);
    const hasEvidences = (obj) =>
      hasTitulares(obj) ||
      hasCodigo(obj) ||
      (typeof obj?.total_pessoas_relacionadas_imovel === 'number' &&
        obj.total_pessoas_relacionadas_imovel > 0);

    if (Array.isArray(sncrData)) {
      for (const item of sncrData) {
        if (item && typeof item === 'object' && hasEvidences(item)) return STATUS.SNCR.REGISTRADA;
      }
      return STATUS.SNCR.NAO_REGISTRADA;
    }
    // objeto único
    return hasEvidences(sncrData) ? STATUS.SNCR.REGISTRADA : STATUS.SNCR.NAO_REGISTRADA;
  } catch (error) {
    if (DEBUG) console.error('[domainStatusExtractor] Erro ao extrair status do SNCR:', error);
    return STATUS.SNCR.NAO_REGISTRADA;
  }
};

/**
 * Extrai o status de todos os domínios diretamente do JSON
 * @param {Object} propertyData - Dados da propriedade do backend
 * @returns {Object} - Objeto com status por domínio
 */
export const extractAllDomainStatus = (propertyData) => {
  // Extrair todos os status
  const resultado = {
    car: extractCarStatus(propertyData),
    sigef: extractSigefStatus(propertyData),
    cafir: extractCafirStatus(propertyData),
    sncr: extractSncrStatus(propertyData),
    ccir: extractCcirStatus(propertyData),
  };

  // Log do resultado completo
  // eslint-disable-next-line no-console
  if (VERBOSE) console.log('[DEBUG] extractAllDomainStatus - Resultado final:', resultado);

  return resultado;
};

/**
 * Determina a cor de status do domínio baseado no tipo de domínio e valor de status
 * @param {string} domain - Domínio (CAR, SIGEF, SNCR, CAFIR)
 * @param {string} status - Status do domínio
 * @returns {string} - Cor Material UI para o status
 */
export const getStatusColor = (domain, status) => {
  if (!status) return COLOR.ERROR;

  const domainKey = domain.toLowerCase();

  // Mapeamento de regras de status para cor por domínio
  const statusColorRules = {
    car: {
      [STATUS.CAR.ATIVO]: COLOR.SUCCESS,
      [STATUS.CAR.PENDENTE]: COLOR.WARNING,
      [STATUS.CAR.SUSPENSO]: COLOR.ERROR,
    },
    sigef: {
      [STATUS.SIGEF.CERTIFICADA]: COLOR.SUCCESS,
      [STATUS.SIGEF.REGISTRADA]: COLOR.WARNING,
      [STATUS.SIGEF.NAO_REGISTRADA]: COLOR.ERROR,
    },
    cafir: {
      [STATUS.CAFIR.REGISTRADA]: COLOR.SUCCESS,
      [STATUS.CAFIR.NAO_REGISTRADA]: COLOR.ERROR,
    },
    sncr: {
      [STATUS.SNCR.REGISTRADA]: COLOR.SUCCESS,
      [STATUS.SNCR.NAO_REGISTRADA]: COLOR.ERROR,
    },
    ccir: {
      [STATUS.CCIR.PRESENTE]: COLOR.SUCCESS,
      [STATUS.CCIR.INEXISTENTE]: COLOR.DEFAULT,
    },
  };

  // Verificar se o domínio existe e retornar a cor correspondente ao status
  return statusColorRules[domainKey]?.[status] || COLOR.DEFAULT;
};
